import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Chip,
  Stack,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { NotificationTemplate } from '../../../types/notification-admin';
import { NotificationType, NotificationPriority } from '../../../types/user';

interface TemplateEditorProps {
  open: boolean;
  template: NotificationTemplate | null;
  mode: 'create' | 'edit';
  onClose: () => void;
  onSave: (data: TemplateFormData) => Promise<void>;
}

export interface TemplateFormData {
  type: NotificationType;
  title: string;
  message: string;
  variables: string[];
  priority: NotificationPriority;
  enabled: boolean;
  changeNotes?: string;
}

/**
 * TemplateEditor Component
 * Dialog for creating and editing notification templates
 */
export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  open,
  template,
  mode,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<TemplateFormData>({
    type: NotificationType.SYSTEM_UPDATE,
    title: '',
    message: '',
    variables: [],
    priority: NotificationPriority.NORMAL,
    enabled: true,
    changeNotes: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof TemplateFormData, string>>>({});
  const [saving, setSaving] = useState(false);
  const [newVariable, setNewVariable] = useState('');
  const [extractedVars, setExtractedVars] = useState<string[]>([]);

  /**
   * Initialize form data when template changes
   */
  useEffect(() => {
    if (template && mode === 'edit') {
      setFormData({
        type: template.type,
        title: template.title,
        message: template.message,
        variables: [...template.variables],
        priority: template.priority,
        enabled: template.enabled,
        changeNotes: '',
      });
    } else if (mode === 'create') {
      setFormData({
        type: NotificationType.SYSTEM_UPDATE,
        title: '',
        message: '',
        variables: [],
        priority: NotificationPriority.NORMAL,
        enabled: true,
        changeNotes: '',
      });
    }
    setErrors({});
  }, [template, mode, open]);

  /**
   * Extract variables from title and message
   */
  useEffect(() => {
    const titleVars = extractVariables(formData.title);
    const messageVars = extractVariables(formData.message);
    const allVars = Array.from(new Set([...titleVars, ...messageVars]));
    setExtractedVars(allVars);
  }, [formData.title, formData.message]);

  /**
   * Extract variable placeholders from text
   */
  const extractVariables = (text: string): string[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1].trim());
    }

    return matches;
  };

  /**
   * Validate form data
   */
  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TemplateFormData, string>> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length > 500) {
      newErrors.message = 'Message must be 500 characters or less';
    }

    // Check for undeclared variables
    const undeclared = extractedVars.filter((v) => !formData.variables.includes(v));
    if (undeclared.length > 0) {
      newErrors.variables = `Undeclared variables: ${undeclared.join(', ')}`;
    }

    if (mode === 'edit' && !formData.changeNotes?.trim()) {
      newErrors.changeNotes = 'Change notes are required for edits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error: any) {
      setErrors({ message: error.message || 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Add a variable to the list
   */
  const handleAddVariable = () => {
    if (newVariable.trim() && !formData.variables.includes(newVariable.trim())) {
      setFormData({
        ...formData,
        variables: [...formData.variables, newVariable.trim()],
      });
      setNewVariable('');
    }
  };

  /**
   * Remove a variable from the list
   */
  const handleRemoveVariable = (variable: string) => {
    setFormData({
      ...formData,
      variables: formData.variables.filter((v) => v !== variable),
    });
  };

  /**
   * Insert variable placeholder into message
   */
  const handleInsertVariable = (variable: string) => {
    const placeholder = `{{${variable}}}`;
    setFormData({
      ...formData,
      message: formData.message + (formData.message ? ' ' : '') + placeholder,
    });
  };

  /**
   * Auto-add extracted variables
   */
  const handleAutoAddVariables = () => {
    const newVars = extractedVars.filter((v) => !formData.variables.includes(v));
    if (newVars.length > 0) {
      setFormData({
        ...formData,
        variables: [...formData.variables, ...newVars],
      });
    }
  };

  /**
   * Get all notification types
   */
  const getAllNotificationTypes = (): NotificationType[] => {
    return Object.values(NotificationType);
  };

  /**
   * Format notification type for display
   */
  const formatType = (type: NotificationType): string => {
    return type
      .toString()
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {mode === 'create' ? 'Create Template' : 'Edit Template'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Notification Type */}
          <FormControl fullWidth disabled={mode === 'edit'}>
            <InputLabel>Notification Type</InputLabel>
            <Select
              value={formData.type}
              label="Notification Type"
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as NotificationType })
              }
            >
              {getAllNotificationTypes().map((type) => (
                <MenuItem key={type} value={type}>
                  {formatType(type)}
                </MenuItem>
              ))}
            </Select>
            {mode === 'edit' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Type cannot be changed after creation
              </Typography>
            )}
          </FormControl>

          {/* Title */}
          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            error={!!errors.title}
            helperText={errors.title || `${formData.title.length}/100 characters`}
            fullWidth
            required
          />

          {/* Message */}
          <TextField
            label="Message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            error={!!errors.message}
            helperText={errors.message || `${formData.message.length}/500 characters`}
            fullWidth
            required
            multiline
            rows={4}
          />

          {/* Variables Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Template Variables
              </Typography>
              <Tooltip title="Variables are placeholders like {{userName}} that get replaced with actual values">
                <IconButton size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Extracted Variables Alert */}
            {extractedVars.length > 0 && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  extractedVars.some((v) => !formData.variables.includes(v)) && (
                    <Button size="small" onClick={handleAutoAddVariables}>
                      Add All
                    </Button>
                  )
                }
              >
                <Typography variant="body2">
                  Found {extractedVars.length} variable(s) in your template:{' '}
                  {extractedVars.join(', ')}
                </Typography>
              </Alert>
            )}

            {/* Variable Error */}
            {errors.variables && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.variables}
              </Alert>
            )}

            {/* Add Variable */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Add Variable"
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddVariable();
                  }
                }}
                placeholder="e.g., userName"
                size="small"
                fullWidth
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddVariable}
                disabled={!newVariable.trim()}
              >
                Add
              </Button>
            </Box>

            {/* Variable List */}
            {formData.variables.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.variables.map((variable) => (
                  <Chip
                    key={variable}
                    label={`{{${variable}}}`}
                    onDelete={() => handleRemoveVariable(variable)}
                    onClick={() => handleInsertVariable(variable)}
                    deleteIcon={<DeleteIcon />}
                    color={extractedVars.includes(variable) ? 'primary' : 'default'}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Priority */}
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              label="Priority"
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value as NotificationPriority })
              }
            >
              <MenuItem value={NotificationPriority.CRITICAL}>Critical</MenuItem>
              <MenuItem value={NotificationPriority.HIGH}>High</MenuItem>
              <MenuItem value={NotificationPriority.NORMAL}>Normal</MenuItem>
              <MenuItem value={NotificationPriority.LOW}>Low</MenuItem>
            </Select>
          </FormControl>

          {/* Enabled Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            }
            label="Template Enabled"
          />

          {/* Change Notes (Edit Mode Only) */}
          {mode === 'edit' && (
            <TextField
              label="Change Notes"
              value={formData.changeNotes}
              onChange={(e) => setFormData({ ...formData, changeNotes: e.target.value })}
              error={!!errors.changeNotes}
              helperText={errors.changeNotes || 'Describe what you changed'}
              fullWidth
              required
              multiline
              rows={2}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
